import type { DeviceClass } from '@/app/lib/guideChat/triggers';
import type { FormStep, GuideChatProduct, PageContext, PageKind } from '@/app/lib/guideChat/types';
import {
  SCREEN_CONTACT,
  SCREEN_INSURANCE,
  SCREEN_LOAN,
  SCREEN_PROFILE_CO,
  SCREEN_PROFILE_MAIN,
  SCREEN_PROJECT,
} from '@/app/components/loanComparator/loan-quotation-steps.constants';

const SCREEN_TO_STEP: Record<number, FormStep> = {
  [SCREEN_PROJECT]: 'project',
  [SCREEN_PROFILE_MAIN]: 'borrower',
  [SCREEN_PROFILE_CO]: 'borrower',
  [SCREEN_LOAN]: 'loan',
  [SCREEN_INSURANCE]: 'bank',
  [SCREEN_CONTACT]: 'identity',
};

const HEALTH_LABEL = /fumeur|m[ée]dical|refus[ée]|ajourn/i;
const RISKY_LABEL = /fumeur|m[ée]dical|refus[ée]|ajourn|profession|activit[ée]|part du pr[êe]t|quotit/i;

export const FUNNEL_STAGE_BY_KIND: Record<PageKind, string> = {
  article: 'discovery',
  landing: 'comparison',
  form: 'quote_started',
};

export function hasQuotationWizard(): boolean {
  return typeof document !== 'undefined' && !!document.querySelector('[data-loan-screen]');
}

export function currentFormStep(): FormStep | null {
  if (typeof document === 'undefined') return null;
  const screens = Array.from(document.querySelectorAll<HTMLElement>('[data-loan-screen]'));
  const visible = screens.find((el) => !el.classList.contains('hidden') && el.offsetParent !== null);
  const screen = Number(visible?.dataset.loanScreen);
  return SCREEN_TO_STEP[screen] ?? null;
}

export function fieldLabel(el: Element | null): string | null {
  const label = el?.closest('.loan-form-field')?.querySelector('label')?.textContent?.trim();
  return label ? label.replace(/\*$/, '').trim() : null;
}

export function activeField(): { name: string | null; label: string | null; element: HTMLElement | null } {
  if (typeof document === 'undefined') return { name: null, label: null, element: null };
  const el = document.activeElement as HTMLElement | null;
  if (!el || !el.closest('[data-loan-screen]')) return { name: null, label: null, element: null };
  const name = (el as HTMLInputElement).name || el.id || el.getAttribute('aria-label') || null;
  return { name: name === 'birthdate' ? 'birthDate' : name, label: fieldLabel(el), element: el };
}

export function isRiskyField(label: string | null): boolean {
  return !!label && RISKY_LABEL.test(label);
}

export function pageTitle(): string {
  if (typeof document === 'undefined') return '';
  const h1 = document.querySelector('h1')?.textContent?.trim();
  return h1 || document.title.replace(/\s*[|–-]\s*R[ée]assurez-moi.*$/i, '').trim();
}

export function buildPageContext(kind: PageKind, product: GuideChatProduct = 'loan'): PageContext {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  if (kind !== 'form') return { kind, url, title: pageTitle(), step: null, field: null, product };
  const { name, label } = activeField();
  const step = currentFormStep();
  return {
    kind,
    url,
    title: pageTitle(),
    step: step === 'borrower' && HEALTH_LABEL.test(label ?? '') ? 'health' : step,
    field: name ?? label,
    product,
  };
}

export function isDesktopPointer(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches && window.innerWidth >= 768;
}

export function deviceClass(): DeviceClass {
  return isDesktopPointer() ? 'desktop' : 'mobile';
}
